import { Grid, Input, Typography } from '@material-ui/core';
import React, { useCallback } from 'react';
import { BaseButton } from 'src/common/components/base-button';
import { getDescriptionAccordingToStoreCulture } from 'src/common/helpers/localized-description-helper';
import { truncateNumberWithPrecision } from 'src/common/helpers/number-helper';
import { IDepartment } from 'src/common/interfaces/dtos/IDepartment';
import { POSModeState } from 'src/common/interfaces/states/POSModeState';
import { store } from 'src/common/redux/configure-store';
import { departmentSaleStyles } from './department-sale-styles';
import { useTranslation } from 'react-i18next';
import { UnitOfMeasureTypes } from 'src/common/enumerations/unit-of-measure-type';

const DepartmentSaleApproval: React.FC<DepartmentSaleApprovalProps> = (
  props: DepartmentSaleApprovalProps,
): JSX.Element => {
  const classes = departmentSaleStyles();
  const { t } = useTranslation();
  const { setItemPrice } = props;
  const onChange = useCallback(
    event => {
      setItemPrice(truncateNumberWithPrecision(event.target.value));
    },
    [setItemPrice],
  );
  const posModeState: POSModeState = store.getState().posMode;

  return (
    <Grid
      item
      container
      direction='row'
      justifyContent='center'
      style={{ flex: 1 }}
      data-testid='department-sale-approval'
    >
      <Grid item container justifyContent='center' alignItems='center' style={{ width: '50%' }}>
        <Grid item container>
          <Grid item container justifyContent='space-between'>
            <Grid item>
              <Typography className={classes.departmentIdLabel}>
                {t('DepartmentSaleView_TitleDepartmentId')}
              </Typography>
            </Grid>
            <Grid item>
              <Typography className={classes.departmentIdValue}>
                {props.department.departmentId}
              </Typography>
            </Grid>
          </Grid>
          <Grid item>
            <Typography className={classes.departmentDescription}>
              {getDescriptionAccordingToStoreCulture(props.department.descriptions)}
            </Typography>
          </Grid>
        </Grid>
        <Grid item container>
          <Grid item xs={6} className={classes.priceLabelContainer}>
            <Typography className={classes.priceLabel}>
              {t('Items')} {t('Price')}{' '}
            </Typography>
          </Grid>
          <Grid item xs={6} container>
            <Grid item>
              <Input
                className={classes.approvalPriceInput}
                classes={{
                  input: classes.inputTextCenter,
                  underline: classes.underline,
                }}
                value={props.itemPrice}
                inputProps={{
                  'data-testid': 'department-sale-price-input',
                  'data-keyboard-owner': 'department-sale-focus',
                }}
                id='price'
                name='price'
                autoComplete='off'
                autoFocus={true}
                onChange={onChange}
              />
            </Grid>
            <Grid item className={classes.priceUnit}>
              <Typography variant={'body2'}>{`  / ${
                props.isManualWeight ? UnitOfMeasureTypes.Kilogram : UnitOfMeasureTypes.Each
              }`}</Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item container justifyContent='center' style={{ alignSelf: 'flex-end' }}>
          <BaseButton
            className={`${classes.button} ${classes.cancelButton}`}
            buttonType='white'
            onClick={props.handleCancel}
            data-testid='department-sale-approval-cancel-button'
          >
            Cancel
          </BaseButton>
          <BaseButton
            className={classes.button}
            buttonType='primary'
            onClick={() => props.handleApprove(props.itemPrice)}
            data-testid='department-sale-approval-button'
          >
            {t(posModeState.returnMode.returnType ? 'Return' : 'Approve')}
          </BaseButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

interface DepartmentSaleApprovalProps {
  department: IDepartment;
  handleApprove: (itemPrice: string) => void;
  handleCancel: () => void;
  isManualWeight: boolean;
  itemPrice: string;
  setItemPrice: (price: string) => void;
}

export default DepartmentSaleApproval;
